export function formatDate(d) {
    return d.toISOString().slice(0, 10);
}

export function toDateTimeLocal(value) {
    if (!value) return "";
    const dt = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dt.getTime())) return "";
    const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

export function toServerDateTime(value) {
    const dt = value instanceof Date ? value : new Date(value);
    if (!dt || Number.isNaN(dt.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const h = pad(dt.getHours());
    const min = pad(dt.getMinutes());
    const s = pad(dt.getSeconds());
    return `${y}-${m}-${d} ${h}:${min}:${s}`;
}

export function getDurationMinutes(start, end, fallback = 60) {
    if (!start || !end) return fallback;
    const diff = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
    return diff > 0 ? diff : fallback;
}

/** FullCalendar `end` is exclusive; API expects inclusive calendar dates. */
export function calendarRangeToApiDates(range) {
    if (!range?.start || !range?.end) return null;
    const startD = new Date(range.start);
    const endExclusive = new Date(range.end);
    const endInclusive = new Date(endExclusive.getTime() - 1);
    if (endInclusive < startD) {
        return { start: formatDate(startD), end: formatDate(startD) };
    }
    return { start: formatDate(startD), end: formatDate(endInclusive) };
}

export function teacherLabelFromClassroom(c) {
    if (!c) return "No teacher";
    const t = c.teacher;
    if (t && typeof t === "object") {
        const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
        return name || "No teacher";
    }
    const first = (c.teacher_first_name || "").trim();
    const last = (c.teacher_last_name || "").trim();
    const name = `${first} ${last}`.trim();
    return name || "No teacher";
}

const DEFAULT_DURATION_MIN = 60;

export function lessonRowToFullCalendarEvent(lesson, classroomById) {
    const c = classroomById.get(Number(lesson.classroom_id));
    const start = new Date(lesson.starts_at);
    const end = lesson.ends_at
        ? new Date(lesson.ends_at)
        : new Date(start.getTime() + DEFAULT_DURATION_MIN * 60000);
    const classroom = c?.name || c?.classroom_name || `Classroom #${lesson.classroom_id}`;
    const status = String(lesson.status || "planned");
    const color = status === "done"
        ? "#198754"
        : status === "in_progress"
          ? "#fd7e14"
          : status === "canceled"
            ? "#dc3545"
            : "#0d6efd";
    const durationMinutes = getDurationMinutes(start, end, DEFAULT_DURATION_MIN);
    const teacher = c?.teacher;
    const teacherName =
        teacher && typeof teacher === "object"
            ? `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim()
            : "";
    const displayTeacher = teacherName || teacherLabelFromClassroom(c);
    const teacherAvatar =
        teacher && typeof teacher === "object" && teacher.avatar ? teacher.avatar : null;

    return {
        id: String(lesson.id),
        title: `#${lesson.id} · ${classroom}`,
        start: start.toISOString(),
        end: end.toISOString(),
        backgroundColor: color,
        borderColor: color,
        textColor: "#fff",
        extendedProps: {
            kind: "lesson",
            lesson_id: lesson.id,
            status: lesson.status || "planned",
            call_type: lesson.call_type || "languageon",
            call_link: lesson.call_link ?? null,
            notes: lesson.notes ?? null,
            attendances: Array.isArray(lesson.attendances) ? lesson.attendances : [],
            classroom_id: lesson.classroom_id,
            classroom_name: classroom,
            display_classroom: classroom,
            display_teacher: displayTeacher,
            teacher_name: teacherName || displayTeacher,
            teacher_avatar: teacherAvatar,
            duration_minutes: durationMinutes,
        },
    };
}

export function getErrorMessage(e, fallback) {
    if (e?.errors && typeof e.errors === "object") {
        const key = Object.keys(e.errors)[0];
        const arr = key ? e.errors[key] : null;
        if (Array.isArray(arr) && arr[0]) return arr[0];
    }
    const body = e?.response?.data || {};
    const key = body.errors ? Object.keys(body.errors)[0] : null;
    const first = key && Array.isArray(body.errors[key]) ? body.errors[key][0] : null;
    return first || body?.message || fallback;
}

export const LESSON_STATUSES = ["planned", "in_progress", "done", "canceled"];
